/* istanbul ignore file */
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { get } from 'underscore';
import {
  ActivityContext,
  ScenarioContext,
} from '@bingads-webui-react/hoc-with-signal';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { AddImageCrudAdComponent } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import {
  getUrlPrefixAndDestination,
  getUpdatedFullUrlOnPrefixUpdate,
  getUpdatedFullUrlOnTextUpdate,
  validateAdUrl,
} from '@bingads-webui-campaign-react/url-input-fluent';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import {
  useAssetLibraryImagePicker,
  useWebsiteSuggestion,
} from '@bingads-webui-campaign-react/add-image-hooks';
import { UploadImageWrapperProps } from './type';

export const UploadImageWrapper = ({
  preferenceService,
  i18n,
  serverContext,
  aid,
  cid,
  maxLimit,
  elementType,
  isBrandKit,
  onSelectImages,
  oDataConfig,
  minSizeConfig,
  panelSubtitle,
  panelTitle,
  permissions,
  instrumentation,
  perfMarker,
  displayImagePicker,
  setDisplayImagePicker,
  selectedMediaPanelSource,
  setSelectedMediaPanelSource,
  disabledAssetSources,
}: UploadImageWrapperProps) => {
  const ref = useRef<any>([]);
  const onNewImageSelected = useCallback(
    (images) => {
      setDisplayImagePicker(false);
      const imageData = Array.isArray(images) ? images.map(image => (Array.isArray(image) ? image[0] : image)) : images;
      // TODO: THIS IS SHORT SOLUTION TO HANDLE UPLOAD IMAGE FROM COMPUTER, WILL REMOVE THIS
      if (ref.current.length === 0) {
        ref.current = imageData;
      } else if (Array.isArray(images) && imageData.length > 0) { ref.current.push(imageData[0]); }
      onSelectImages(ref.current, elementType);
    },
    [setDisplayImagePicker, onSelectImages, elementType]
  );

  const activity = useContext(ActivityContext) as { signal: (message: string) => void };
  const scenario = useContext(ScenarioContext) as { name: string };
  const [privateUrl, setPrivateUrl] = useState('');

  const url = privateUrl || 'https://';

  const urlPrefixValue = useMemo(() => {
    const [prefix] = getUrlPrefixAndDestination(url);
    return prefix;
  }, [url]);

  const { suggestionState, suggestionPoller } = useWebsiteSuggestion({
    oDataConfig,
    adContext: {
      advertiserCustomerId: get(serverContext, ['CurrentCustomer', 'Id']),
      accountId: aid,
    },
    instrumentation,
    logActivity: activity.signal,
    permissions,
    fullUrl: url,
    hasErrorInUrl: validateAdUrl(url) !== null,
    adCreatorScenario: {
      scenarioName: `${scenario.name}AdCreation`,
      scenarioValue: `${scenario.name}AdCreation`,
    },
    // headlines: [],
    // descriptions: [],
  });

  const updateUrl = (evt) => {
    const { value } = evt.target;
    const newUrl = getUpdatedFullUrlOnTextUpdate(urlPrefixValue, value);
    setPrivateUrl(newUrl);
  };

  const updateUrlPrefix = (evt, { text }) => {
    const newPrefix = text.substring(0, text.length - 3);
    const [, original] = getUrlPrefixAndDestination(url);
    const newUrl = getUpdatedFullUrlOnPrefixUpdate(original, newPrefix);
    setPrivateUrl(newUrl);
  };
  const {
    imagePickerError,
    setImagePickerError,
    clearImagePickerError,
    uploadImage,
    processUploadedImage,
    minSize,
    addImageByUrl,
    onImageSelected,
    onImagesSelected,
    customMediaSourceConfig,
  } = useAssetLibraryImagePicker({
    cid: get(serverContext, ['CurrentCustomer', 'Id']),
    aid: aid.toString(),
    oDataConfig,
    onNewImageSelected,
    i18n,
    permissions,
    isDisplayAds: true,
    isBrandKit,
  });
  const emptyFinalUrl = !url || url === `${urlPrefixValue}://`;
  return (
    <AddImageCrudAdComponent
      isOpen={displayImagePicker}
      setIsOpen={(newOpen) => {
        setDisplayImagePicker(newOpen);
        if (!newOpen) {
          clearImagePickerError();
        }
      }}
      panelSubtitle={panelSubtitle}
      panelTitle={panelTitle}
      uploadImage={uploadImage}
      processUploadedImage={processUploadedImage}
      minSize={minSizeConfig || minSize}
      showPromotionBanner={false}
      isStockImageEnabled={false}
      maxUploadNum={maxLimit}
      imageExperienceProps={{
        suggestion: get(suggestionState, ['suggestion']),
        suggestionPoller,
        finalUrl: emptyFinalUrl ? '' : url,
        finalUrlProtocol: `${urlPrefixValue}://`,
        onUpdateFinalUrl: updateUrl,
        onUpdateFinalUrlPrefix: updateUrlPrefix,
        isMultimediaAd: false,
        imageOption: constants.defaultImageOption,
        // todo: what to add here
        isDisplayAds: true,
        isBrandKit,
      }}
      addImageByUrl={addImageByUrl}
      managedViewSelectionState={selectedMediaPanelSource}
      updateManagedViewSelectionState={setSelectedMediaPanelSource}
      assetLibraryConfig={{
        cid: cid.toString(),
        aid: aid.toString(),
        oDataConfig,
        dateFormat: get(serverContext, ['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern']),
        timeZone: get(serverContext, ['CurrentUser', 'TimeZone']),
        preferencesService: preferenceService,
        activityLogger: activity,
        onImageSelected,
        onImagesSelected,
        onImageAddError: e => setImagePickerError(e),
        enableStockMultipleSelection: true,
        enableWebsiteMultipleSelection: true,
      }}
      i18n={i18n}
      cid={cid}
      aid={aid}
      errorMessage={imagePickerError}
      imageType={null}
      customMediaSourceConfig={customMediaSourceConfig}
      perfMarker={perfMarker}
      permissions={permissions}
      isAADUser={get(serverContext, ['IsAADUser'])}
      creativeEditorProps={null}
      serverContext={serverContext}
      disabledAssetSources={disabledAssetSources}
    />
  );
};

export const UploadImage = withInstrumentation(UploadImageWrapper);
